import { Text } from "@certn/cascadia";
import {
  Card,
  GhostIconButton,
  PhoneNumberInput,
  TextField,
  mq,
  useEmailSelector,
} from "@certn/cascadia-react";
import type { PhoneNumberValidity } from "@certn/cascadia-react";
import { css, cx } from "@emotion/css";
import { useField, useFormikContext } from "formik";
import { useEffect, useId, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import {
  NotificationChannel,
  Order,
  RecipientMobileNumber,
} from "../../OrderContext";
import { selectedEnabledNotificationChannels } from "../../order-utils";

interface RecipientsTableFieldProps {
  description?: string;
  enabledChannels: NotificationChannel[];
  max?: number;
}

export function RecipientsTableField({
  description,
  enabledChannels,
  max,
}: RecipientsTableFieldProps) {
  const { t } = useTranslation();
  const headingId = useId();
  const descriptionId = useId();
  const emailErrorId = useId();
  const { values, setValues } = useFormikContext<Order>();
  const [, recipientsMeta] = useField("recipients");
  const channels = selectedEnabledNotificationChannels(
    values.notification_channels,
    enabledChannels
  );
  const showMobile = channels.length > 0;

  const handleEmailsChange = (emails: string[]) => {
    setValues((prev) => {
      const current = prev.recipient_mobile_numbers ?? {};
      const recipientMobileNumbers = Object.fromEntries(
        emails
          .filter((email) => current[email])
          .map((email) => [email, current[email]])
      );
      return {
        ...prev,
        recipients: emails,
        recipient_mobile_numbers: recipientMobileNumbers,
      };
    });
  };

  const updateMobileNumber = (email: string, entry: RecipientMobileNumber) => {
    setValues((prev) => {
      const current = prev.recipient_mobile_numbers?.[email];
      if (
        current?.number === entry.number &&
        current?.status === entry.status
      ) {
        return prev;
      }
      return {
        ...prev,
        recipient_mobile_numbers: {
          ...(prev.recipient_mobile_numbers ?? {}),
          [email]: entry,
        },
      };
    });
  };

  const {
    emails,
    inputValue,
    isValid,
    removeEmail,
    handleInputChange,
    handleKeyDown,
    handleBlur,
    handlePasteEvent,
  } = useEmailSelector({
    initialEmails: values.recipients ?? [],
    onChange: handleEmailsChange,
    max,
  });

  const [emailDraftAttempted, setEmailDraftAttempted] = useState(false);
  const isDuplicateDraft = inputValue.length > 0 && emails.includes(inputValue);
  // isValid is false for an incomplete draft and for a duplicate. Name the
  // duplicate immediately. Hold the format message until Enter or blur so a
  // partial address is not flagged while it is being typed.
  const emailError = isDuplicateDraft
    ? t("Every email must be unique.")
    : emailDraftAttempted && !isValid
      ? t("One or more entries is not using a valid email format.")
      : recipientsMeta.touched && recipientsMeta.error
        ? recipientsMeta.error
        : undefined;

  const handleEmailInputChange = (value: string) => {
    if (value.length === 0 || inputValue.length === 0) {
      setEmailDraftAttempted(false);
    }
    handleInputChange(value);
  };

  const markEmailDraftAttempted = () => {
    if (inputValue.length > 0) {
      setEmailDraftAttempted(true);
    }
  };

  const isAtMax = typeof max === "number" && emails.length >= max;
  const describedBy = [
    description ? descriptionId : null,
    emailError ? emailErrorId : null,
  ]
    .filter(Boolean)
    .join(" ");

  const focusTarget = useRef<string | null>(null);
  const seenEmails = useRef(emails);
  const [statusMessage, setStatusMessage] = useState("");

  useEffect(() => {
    const previous = seenEmails.current;
    seenEmails.current = emails;
    if (previous === emails) {
      return;
    }
    const added = emails.filter((email) => !previous.includes(email));
    const removed = previous.filter((email) => !emails.includes(email));
    if (removed.length > 0) {
      setStatusMessage(t("Removed {{email}}.", { email: removed.join(", ") }));
    } else if (added.length === 1 && emails.length === 1) {
      setStatusMessage(t("Added {{email}}. 1 recipient.", { email: added[0] }));
    } else if (added.length > 0) {
      setStatusMessage(
        t("Added {{email}}. {{count}} recipients.", {
          email: added.join(", "),
          count: emails.length,
        })
      );
    }

    const target = focusTarget.current;
    if (!target) {
      return;
    }
    focusTarget.current = null;
    const element =
      target === "add"
        ? document.querySelector<HTMLElement>(
            "[data-testid='recipients-table-email-input']"
          )
        : document.querySelector<HTMLElement>(
            `[data-testid="remove-recipient-${CSS.escape(target)}"]`
          );
    element?.focus();
  }, [emails, t]);

  const removeRecipient = (email: string) => {
    const index = emails.indexOf(email);
    const remaining = emails.filter((item) => item !== email);
    focusTarget.current = remaining[index] ?? remaining[index - 1] ?? "add";
    removeEmail(email);
  };

  const columnsClass = showMobile
    ? styles.withMobileColumns
    : styles.emailColumns;

  return (
    <div className={styles.section}>
      <div
        aria-live="polite"
        aria-atomic="true"
        className={styles.visuallyHidden}
      >
        {statusMessage}
      </div>
      <h3 id={headingId} className={styles.heading}>
        <Text style="global/heading-medium" testID="recipients-send-to-heading">
          {t("Send to")} ({emails.length})
        </Text>
      </h3>
      <Card testID="recipients-table" border="border-base" type="large">
        <div role="table" aria-labelledby={headingId} className={styles.table}>
          <div role="row" className={cx(styles.header, columnsClass)}>
            <div role="columnheader">
              <Text style="input/section-title" color="text-weak">
                {t("Email")}
              </Text>
            </div>
            {showMobile && (
              <div role="columnheader">
                <Text
                  style="input/section-title"
                  color="text-weak"
                  testID="recipients-mobile-header"
                >
                  {t("Mobile (optional)")}
                </Text>
              </div>
            )}
            <div
              role="columnheader"
              className={showMobile ? styles.removeHeader : undefined}
            >
              <span className={styles.visuallyHidden}>{t("Remove")}</span>
            </div>
          </div>
          {emails.map((email) => (
            <RecipientRow
              key={email}
              email={email}
              entry={values.recipient_mobile_numbers?.[email]}
              showMobile={showMobile}
              multipleRecipients={emails.length > 1}
              onRemove={() => removeRecipient(email)}
              onMobileChange={updateMobileNumber}
            />
          ))}
          {!isAtMax && (
            <div role="row" className={styles.addRow}>
              <div role="cell" aria-colspan={showMobile ? 3 : 2}>
                <TextField
                  aria-label={
                    emails.length === 0
                      ? t("Recipient email")
                      : t("Add another email")
                  }
                  aria-describedby={describedBy || undefined}
                  placeholder={
                    emails.length === 0
                      ? t("name@example.com")
                      : t("Add another email")
                  }
                  testID="recipients-table-email-input"
                  value={inputValue}
                  onChange={handleEmailInputChange}
                  onKeyDown={(event) => {
                    if (event.key === "Enter") {
                      event.preventDefault();
                      markEmailDraftAttempted();
                    }
                    handleKeyDown(event);
                  }}
                  onBlur={() => {
                    markEmailDraftAttempted();
                    handleBlur();
                  }}
                  onPaste={handlePasteEvent}
                  isInvalid={Boolean(emailError)}
                />
                <div id={emailErrorId} aria-live="polite">
                  {emailError ? (
                    <Text style="input/inline-error" color="text-danger">
                      {emailError}
                    </Text>
                  ) : null}
                </div>
              </div>
            </div>
          )}
        </div>
      </Card>
      {description && (
        <span id={descriptionId}>
          <Text style="input/helper" color="text-weak">
            {description}
          </Text>
        </span>
      )}
    </div>
  );
}

function RecipientRow({
  email,
  entry,
  showMobile,
  multipleRecipients,
  onRemove,
  onMobileChange,
}: {
  email: string;
  entry?: RecipientMobileNumber;
  showMobile: boolean;
  multipleRecipients: boolean;
  onRemove: () => void;
  onMobileChange: (email: string, entry: RecipientMobileNumber) => void;
}) {
  const { t } = useTranslation();
  const latestNumber = useRef<string | null>(entry?.number ?? null);

  return (
    <div
      role="row"
      className={cx(
        styles.row,
        showMobile ? styles.withMobileColumns : styles.emailColumns
      )}
    >
      <div role="cell" className={styles.emailCell}>
        <Text style="input/text-field" truncate className={styles.emailText}>
          {email}
        </Text>
      </div>
      <div role="cell" className={styles.phoneField} hidden={!showMobile}>
        <PhoneNumberInput
          name={`recipient-mobile-${email}`}
          type="string"
          label={t("Mobile number for {{email}} (optional)", { email })}
          invalidMessage={
            multipleRecipients
              ? t("Please enter a valid phone number for {{email}}.", {
                  email,
                })
              : undefined
          }
          countryAriaLabel={t("Country code for {{email}}", { email })}
          value={entry?.number ?? ""}
          testID={`recipient-mobile-${email}`}
          onChange={(value) => {
            latestNumber.current = typeof value === "string" ? value : null;
          }}
          onValidityChange={(status: PhoneNumberValidity) => {
            const number = status === "valid" ? latestNumber.current : null;
            if (
              status === "empty" &&
              !number &&
              (entry == null || (entry.status === "empty" && !entry.number))
            ) {
              return;
            }
            if (entry?.status === status && entry.number === number) {
              return;
            }
            onMobileChange(email, { number, status });
          }}
        />
      </div>
      <div role="cell" className={styles.removeCell}>
        <GhostIconButton
          icon="close"
          size="small"
          aria-label={t("Remove {{email}}", { email })}
          testID={`remove-recipient-${email}`}
          onPress={onRemove}
        />
      </div>
    </div>
  );
}

const styles = {
  heading: css({ margin: 0 }),
  visuallyHidden: css({
    border: 0,
    clip: "rect(0 0 0 0)",
    clipPath: "inset(50%)",
    height: "1px",
    margin: "-1px",
    overflow: "hidden",
    padding: 0,
    position: "absolute",
    width: "1px",
    whiteSpace: "nowrap",
  }),
  section: css({
    display: "flex",
    flexDirection: "column",
    gap: "var(--space-3)",
  }),
  table: css({
    minWidth: 0,
  }),
  header: css({
    display: "grid",
    position: "relative",
    alignItems: "center",
    columnGap: "var(--space-4)",
    background: "var(--background-accent-neutral-weak)",
    padding: "var(--space-3) var(--space-4)",
    textTransform: "uppercase",
  }),
  row: css({
    display: "grid",
    alignItems: "center",
    columnGap: "var(--space-4)",
    padding: "var(--space-3) var(--space-4)",
    borderTop: "1px solid var(--border-base)",
    minWidth: 0,
  }),
  // The narrow order form is full width below 490px, and Card clips overflow.
  // A 240px phone column plus the remove control does not fit there, so stack
  // in source order (email, phone, remove). That keeps keyboard order lined
  // up with the stacked layout. The side-by-side table starts at 490px.
  emailColumns: css({
    gridTemplateColumns: "minmax(0, 1fr) auto",
  }),
  withMobileColumns: css(
    mq({
      gridTemplateColumns: [
        "minmax(0, 1fr)",
        "minmax(0, 1.4fr) minmax(240px, 1fr) auto",
      ],
      rowGap: ["var(--space-3)", 0],
    })
  ),
  // The remove header is an empty accessible name. Take it out of the stacked
  // header so it does not add a blank row; it returns to the third column
  // once the row is wide enough for the table.
  removeHeader: css(
    mq({
      position: ["absolute", "static"],
      width: ["1px", "auto"],
      height: ["1px", "auto"],
      overflow: ["hidden", "visible"],
      clip: ["rect(0 0 0 0)", "auto"],
    })
  ),
  removeCell: css({
    justifySelf: "end",
  }),
  emailCell: css({
    minWidth: 0,
    overflow: "hidden",
  }),
  emailText: css({
    maxWidth: "100%",
  }),
  addRow: css({
    padding: "var(--space-3) var(--space-4)",
    borderTop: "1px solid var(--border-base)",
  }),
  // The column header already names the field. The input label stays in the
  // accessibility tree so each row is announced with its email.
  phoneField: css({
    position: "relative",
    minWidth: 0,
    "&[hidden]": {
      display: "none",
    },
    "& [data-testid='form-label']": {
      border: 0,
      clip: "rect(0 0 0 0)",
      clipPath: "inset(50%)",
      height: "1px",
      margin: "-1px",
      overflow: "hidden",
      padding: 0,
      position: "absolute",
      width: "1px",
      whiteSpace: "nowrap",
    },
  }),
};
